"use client";

// „Der Weg eines Themas" — drei Beispiel-Workflows gleichzeitig sichtbar.
// Jeder Workflow verbindet genau ein Modul pro Phase mit rechtwinkligen
// Linien (HVH) und läuft als gerichteter Signal-Dash entlang der Kante.
// Keine wandernden Bubbles — die Animation lebt aus Chip-Pulse + Line-Dash.

import { useLayoutEffect, useRef, useState } from "react";
import {
  Radar,
  CalendarDays,
  Sparkles,
  Radio,
  ShieldCheck,
  AudioLines,
  Megaphone,
  Share2,
  Bot,
  UserCheck,
  Smartphone,
  Server,
  Mic,
  CloudSun,
  Calendar,
  Lightbulb,
} from "lucide-react";

type Module = { icon: typeof Radar; label: string };
type Phase = {
  num: string;
  title: string;
  claim: string;
  modules: Module[];
};

const PHASES: Phase[] = [
  {
    num: "01",
    title: "Planen",
    claim: "Themen, Beiträge und Slots koordinieren — an einem Ort.",
    modules: [
      { icon: Calendar, label: "Sendungsplaner" },
      { icon: Megaphone, label: "Promotionplaner" },
      { icon: Lightbulb, label: "Themenplaner" },
      { icon: Share2, label: "Social Media Planer" },
      { icon: CalendarDays, label: "Stimmungsplaner" },
    ],
  },
  {
    num: "02",
    title: "Entdecken",
    claim: "Neue Themen erkennen und prüfen — bevor sie Mainstream werden.",
    modules: [
      { icon: Radar, label: "Themen-Radar" },
      { icon: Bot, label: "AI-Research Agent" },
      { icon: Sparkles, label: "AI-Briefings" },
      { icon: ShieldCheck, label: "Faktencheck" },
    ],
  },
  {
    num: "03",
    title: "Produzieren",
    claim: "Inhalte erstellen, prüfen, freigeben — redaktionell geführt.",
    modules: [
      { icon: Sparkles, label: "AI-Briefings" },
      { icon: Mic, label: "AI Opener" },
      { icon: AudioLines, label: "Text-to-Speech" },
      { icon: UserCheck, label: "Human-in-the-Loop" },
      { icon: CloudSun, label: "Wetter & Verkehr" },
      { icon: Share2, label: "Social Media Content" },
    ],
  },
  {
    num: "04",
    title: "Playout",
    claim: "On-Air, Promo und Social — als ein verbundener Ausspielprozess.",
    modules: [
      { icon: Radio, label: "On Air" },
      { icon: Smartphone, label: "Radio APP" },
      { icon: Server, label: "DABiS800" },
      { icon: Share2, label: "Social Media" },
    ],
  },
];

// Drei Beispiel-Flows durch die Phasen
type Workflow = { id: string; label: string; color: string; chips: [string, string, string, string] };
const WORKFLOWS: Workflow[] = [
  {
    id: "morning",
    label: "Morgenmagazin",
    color: "#1E3AFF",
    chips: ["Sendungsplaner", "Themen-Radar", "AI-Briefings", "On Air"],
  },
  {
    id: "breaking",
    label: "Breaking News",
    color: "#E86A2C",
    chips: ["Themenplaner", "AI-Research Agent", "Text-to-Speech", "Radio APP"],
  },
  {
    id: "weather",
    label: "Wetter & Verkehr",
    color: "#10B981",
    chips: ["Stimmungsplaner", "Faktencheck", "Wetter & Verkehr", "DABiS800"],
  },
];

type Point = { x: number; y: number };

// Findet den Workflow (falls vorhanden), der diesen Chip in dieser Phase highlightet.
function workflowForChip(phaseIdx: number, label: string): Workflow | null {
  return WORKFLOWS.find((w) => w.chips[phaseIdx] === label) ?? null;
}

export function ThemeJourney() {
  const railRef = useRef<HTMLDivElement | null>(null);
  // 3 Workflows × 4 Phasen = 12 Chip-Refs
  const chipRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const [points, setPoints] = useState<Record<string, Point[]>>({});
  const [railSize, setRailSize] = useState({ w: 0, h: 0 });
  // Aktiv-Hover: zeigt nur den hovered Workflow an. null = keiner sichtbar.
  const [activeWf, setActiveWf] = useState<string | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const rail = railRef.current;
      if (!rail) return;
      const rr = rail.getBoundingClientRect();
      setRailSize({ w: rr.width, h: rr.height });
      const all: Record<string, Point[]> = {};
      for (const w of WORKFLOWS) {
        const pts: Point[] = [];
        for (let phaseIdx = 0; phaseIdx < 4; phaseIdx++) {
          const key = `${w.id}-${phaseIdx}`;
          const el = chipRefs.current[key];
          if (!el) return;
          const r = el.getBoundingClientRect();
          pts.push({
            x: r.left - rr.left + r.width / 2,
            y: r.top - rr.top + r.height / 2,
          });
        }
        all[w.id] = pts;
      }
      setPoints(all);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (railRef.current) ro.observe(railRef.current);
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, []);

  // HVH-Pfad von Punkt A nach B: rechts bis midX, dann vertikal zu y2, dann zu x2
  const orthogonalPath = (pts: Point[]): string => {
    if (pts.length < 2) return "";
    const parts: string[] = [`M ${pts[0].x} ${pts[0].y}`];
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const midX = (a.x + b.x) / 2;
      parts.push(`H ${midX}`);
      parts.push(`V ${b.y}`);
      parts.push(`H ${b.x}`);
    }
    return parts.join(" ");
  };

  return (
    <section id="features" className="relative py-[140px] overflow-hidden">
      {/* Intro */}
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-12 mb-16">
        <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-[var(--color-blue)] mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[var(--color-blue)]" />
          <span>04 — Der Weg eines Themas</span>
        </div>
        <h2 className="font-serif font-medium text-[clamp(40px,5vw,64px)] leading-[1.1] tracking-[-0.02em] max-w-[900px] mb-8">
          Ein Thema, ein verbundener Prozess.
        </h2>
        <p className="text-[20px] leading-[1.55] text-[var(--color-ink-soft)] max-w-[680px]">
          Drei typische Abläufe durch Orchestra — Morgenmagazin, Breaking News,
          Wetter &amp; Verkehr. Jedes Thema wandert durch die vier Phasen,
          jede Sendung hat ihren eigenen Weg.
        </p>

        {/* Legende — hover zeigt den jeweiligen Workflow-Pfad */}
        <div className="hidden lg:flex flex-wrap gap-5 mt-8">
          {WORKFLOWS.map((w) => {
            const active = activeWf === w.id;
            return (
              <button
                key={w.id}
                type="button"
                onMouseEnter={() => setActiveWf(w.id)}
                onMouseLeave={() => setActiveWf(null)}
                className="group flex items-center gap-2.5 text-[13px] text-[var(--color-ink-soft)] transition-opacity"
                style={{ opacity: activeWf && !active ? 0.35 : 1 }}
              >
                <span
                  className="w-7 h-[3px] rounded-full transition-all"
                  style={{
                    background: w.color,
                    transform: active ? "scaleX(1.2)" : "scaleX(1)",
                  }}
                />
                <span className="font-medium text-[var(--color-ink)]">{w.label}</span>
              </button>
            );
          })}
        </div>

        {/* Hinweis-Zeile */}
        <p className="hidden lg:block mt-3 text-[12px] text-[var(--color-ink-muted)]">
          Fahre mit der Maus über einen Workflow oder Baustein, um den Ablauf zu sehen.
        </p>
      </div>

      {/* Phase-Rail */}
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-12">
        <div className="relative" ref={railRef}>
          {/* Dezenter Base-Strich hinter den Phasen-Köpfen */}
          <div
            aria-hidden
            className="hidden lg:block absolute top-[26px] left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, var(--color-line) 8%, var(--color-line) 92%, transparent 100%)",
            }}
          />

          {/* Workflow-Overlay: 3 rechtwinklige SVG-Pfade */}
          {railSize.w > 0 && Object.keys(points).length === WORKFLOWS.length && (
            <svg
              aria-hidden
              className="hidden lg:block absolute inset-0 pointer-events-none"
              width={railSize.w}
              height={railSize.h}
              viewBox={`0 0 ${railSize.w} ${railSize.h}`}
            >
              {WORKFLOWS.map((w, i) => {
                if (activeWf !== w.id) return null;
                const d = orthogonalPath(points[w.id] || []);
                if (!d) return null;
                return (
                  <g key={w.id}>
                    {/* Feine durchgezogene Linie als Verbindung */}
                    <path
                      d={d}
                      fill="none"
                      stroke={w.color}
                      strokeWidth="0.6"
                      strokeLinejoin="round"
                      opacity="0.8"
                    />
                    {/* Runde Bead-Dots entlang der Linie (mit marchierender Bewegung) */}
                    <path
                      d={d}
                      fill="none"
                      stroke={w.color}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray="0 20"
                      className={`wf-line wf-line-${i}`}
                    />
                  </g>
                );
              })}
            </svg>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {PHASES.map((p, phaseIdx) => (
              <div key={p.num} className="relative flex flex-col lg:max-w-[200px]">
                {/* Phase-Kopf */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="relative w-[13px] h-[13px] rounded-full bg-white border-2 border-[var(--color-blue)] shrink-0">
                    <span className="absolute inset-[2px] rounded-full bg-[var(--color-blue)]" />
                  </div>
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--color-ink-muted)]">
                    Phase {p.num}
                  </span>
                </div>

                {/* Titel */}
                <h3 className="font-serif font-medium text-[26px] leading-[1.15] tracking-[-0.02em] mb-3">
                  {p.title}
                </h3>

                {/* Claim */}
                <p className="text-[14px] leading-[1.55] text-[var(--color-ink-soft)] mb-6 max-w-[240px]">
                  {p.claim}
                </p>

                {/* Module-Chips — Highlight-Chips tragen Ref und pulsierende Outline */}
                <ul className="flex flex-col gap-2 mt-auto">
                  {p.modules.map((m) => {
                    const Icon = m.icon;
                    const wf = workflowForChip(phaseIdx, m.label);
                    const isHighlight = !!wf && activeWf === wf.id;
                    const isWfChip = !!wf;
                    return (
                      <li
                        key={`${p.num}-${m.label}`}
                        ref={(el) => {
                          if (wf) chipRefs.current[`${wf.id}-${phaseIdx}`] = el;
                        }}
                        onMouseEnter={isWfChip ? () => setActiveWf(wf!.id) : undefined}
                        onMouseLeave={isWfChip ? () => setActiveWf(null) : undefined}
                        className={`relative flex items-center gap-3 rounded-full pl-2.5 pr-5 py-2.5 text-[13px] bg-white text-[var(--color-ink)] transition-colors ${
                          isHighlight ? "wf-chip" : "border border-[var(--color-line)] hover:border-[var(--color-blue)]/40"
                        }`}
                        style={
                          isHighlight
                            ? { ["--wf-color" as string]: wf!.color }
                            : undefined
                        }
                      >
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                          style={{
                            background: isHighlight ? `${wf!.color}18` : "var(--color-bg)",
                            border: isHighlight ? "none" : "1px solid var(--color-line)",
                          }}
                        >
                          <Icon
                            size={14}
                            strokeWidth={1.8}
                            style={{ color: isHighlight ? wf!.color : "var(--color-blue)" }}
                          />
                        </span>
                        <span className="truncate">{m.label}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* Mobile/Tablet-Pfeil */}
                {phaseIdx < PHASES.length - 1 && (
                  <div aria-hidden className="lg:hidden flex justify-center mt-8 text-[var(--color-ink-muted)]">
                    <svg width="16" height="24" viewBox="0 0 16 24" fill="none">
                      <path d="M8 0v22M2 16l6 6 6-6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Marchierender Dash auf den 3 Workflow-Linien, unterschiedlich schnell für Leben */
        :global(.wf-line) {
          animation: wf-march linear infinite;
        }
        :global(.wf-line-0) { animation-duration: 2.4s; }
        :global(.wf-line-1) { animation-duration: 3.1s; animation-direction: reverse; }
        :global(.wf-line-2) { animation-duration: 2.8s; }
        @keyframes wf-march {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -20; }
        }

        /* Chip-Outline: gestrichelte Border in Workflow-Farbe, statisch */
        :global(.wf-chip) {
          border: 1.5px dashed var(--wf-color);
        }

        @media (prefers-reduced-motion: reduce) {
          :global(.wf-line) { animation: none; }
        }
      `}</style>
    </section>
  );
}
